// B-80 — the pure half of "Rehacer": what the confirmation says will be lost, for one closing
// (`previewCierreRedo`) and for a whole day (`previewCierreDayRedo`), in the operator's words.
//
// The backend answers keys and values; the words come from the client's dictionary (B-47) here,
// so nothing in this file names a client or a key. Nothing is deleted by a redo — the history stays
// on the closing — and the confirmation says so: what it lists stops counting.

import { correctionError } from "./cierreCorrections";
import { fechaDMY } from "./cierrePanel";
import { nombreDeAlerta, nombreDeDato, nombreDeFuente, valorLegible } from "./cierreNombres";

const fechaCorta = (iso) => {
  if (!iso) return "";
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return "";
  return fecha.toLocaleDateString("es-CO", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Bogota" });
};

// A stored value as the confirmation shows it. The preview carries no field type, so a number is
// grouped (it is pesos almost always, and a count reads fine grouped) — never guessed into "$".
const valor = (v) => (v === null || v === undefined ? "—" : valorLegible(v, Number.isFinite(Number(v)) ? "count" : undefined));

const archivoDe = (actual) => actual?.originalFilename || actual?.filename || null;

// One correction: what it is now, who typed it and why, and what it goes back to.
const lineaDeCorreccion = (c, nombres, oficina) => ({
  titulo: `${oficina ? `${oficina} · ` : ""}${nombreDeDato(nombres, c.datapointKey)}: ${valor(c.value)}`,
  detalle: [
    [c.recordedBy, fechaCorta(c.recordedAt)].filter(Boolean).join(", "),
    c.reason ? `Motivo: ${c.reason}` : null,
    c.restoresTo === null || c.restoresTo === undefined ? "→ queda vacío" : `→ vuelve a ${valor(c.restoresTo)} (del reporte)`,
  ].filter(Boolean).join(" · "),
});

const lineaDeAlerta = (a, nombres, oficina) => ({
  titulo: `${oficina ? `${oficina} · ` : ""}${nombreDeAlerta(nombres, a.ruleKey)}`,
  detalle: [
    [a.resolvedBy ? `Atendida por ${a.resolvedBy}` : "Atendida", fechaCorta(a.resolvedAt)].filter(Boolean).join(", "),
    a.observation ? `Observación: “${a.observation}”` : null,
  ].filter(Boolean).join(" · "),
});

// The files a chosen upload replaces — only the keys with a file chosen.
const lineasDeArchivos = (documentos, nombres, elegidos) => (documentos || [])
  .filter((d) => elegidos?.[d.documentKey])
  .map((d) => ({
    titulo: nombreDeFuente(nombres, d.documentKey),
    detalle: `${archivoDe(d.current) || "sin archivo"} → ${elegidos[d.documentKey].name}`,
  }));

const recortar = (lineas, max) => (max && lineas.length > max
  ? [...lineas.slice(0, max), { titulo: `y ${lineas.length - max} más`, detalle: "" }]
  : lineas);

// The sections of the confirmation, the empty ones left out. `elegidos` is `{ key: File | null }`.
export function seccionesDeRehacer(preview, nombres, elegidos = {}) {
  if (!preview) return [];
  return [
    { clave: "archivos", titulo: "Archivos que se reemplazan", lineas: lineasDeArchivos(preview.documents, nombres, elegidos) },
    {
      clave: "correcciones",
      titulo: `Correcciones manuales (${(preview.corrections || []).length})`,
      lineas: (preview.corrections || []).map((c) => lineaDeCorreccion(c, nombres)),
    },
    {
      clave: "alertas",
      titulo: `Alertas atendidas (${(preview.attendedAlerts || []).length})`,
      lineas: (preview.attendedAlerts || []).map((a) => lineaDeAlerta(a, nombres)),
    },
  ].filter((s) => s.lineas.length);
}

// The same for a whole day, each line naming its office; long lists are cut with "y N más".
export function seccionesDelDia(preview, nombres, elegidos = {}, { max = 20 } = {}) {
  if (!preview) return [];
  const oficina = (x) => x.officeCode || "";
  return [
    { clave: "archivos", titulo: "Reportes que se reemplazan", lineas: lineasDeArchivos(preview.documents, nombres, elegidos) },
    {
      clave: "correcciones",
      titulo: `Correcciones manuales (${(preview.corrections || []).length})`,
      lineas: recortar((preview.corrections || []).map((c) => lineaDeCorreccion(c, nombres, oficina(c))), max),
    },
    {
      clave: "alertas",
      titulo: `Alertas atendidas (${(preview.attendedAlerts || []).length})`,
      lineas: recortar((preview.attendedAlerts || []).map((a) => lineaDeAlerta(a, nombres, oficina(a))), max),
    },
  ].filter((s) => s.lineas.length);
}

// What the day's redo reaches, in one sentence.
export function resumenDelDia(preview) {
  if (!preview) return "";
  const cerradas = preview.terminal || 0;
  const intactas = (preview.untouched?.running || 0) + (preview.untouched?.cancelled || 0);
  return [
    `Este día (${fechaDMY(preview.businessDate)}) ya se procesó. Se rehacen sus ${preview.closings} oficinas`
      + `${cerradas ? `, también las ${cerradas} cerradas` : ""}.`,
    "Los reportes que no envíes se toman de la carga anterior.",
    intactas ? `${intactas} ${intactas === 1 ? "oficina no se toca" : "oficinas no se tocan"} (las trabaja un agente o están canceladas).` : null,
  ].filter(Boolean).join(" ");
}

// A refusal of a redo, in the operator's words; the backend's own sentence otherwise.
export function errorDeRehacer(response) {
  if (response?.errorCode === "LIMIT_FILE_SIZE") return "Uno de los archivos supera el límite de 20 MB.";
  return correctionError(response);
}
